import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

export default function StudioManager() {
  const queryClient = useQueryClient();

  const { data: contents = [] } = useQuery({
    queryKey: ['studio-content'],
    queryFn: () => base44.entities.StudioContent.list(),
  });

  const saveContent = useMutation({
    mutationFn: async ({ key, content }) => {
      const existing = contents.find(c => c.key === key);
      if (existing) {
        return base44.entities.StudioContent.update(existing.id, { content });
      } else {
        return base44.entities.StudioContent.create({ key, content });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['studio-content']);
      toast.success('Contenuto salvato');
    },
  });

  const handleFileUpload = async (key, file) => {
    if (!file) return;
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      saveContent.mutate({ key, content: file_url });
    } catch (error) {
      toast.error('Errore nel caricamento');
    }
  };

  const getContent = (key) => {
    return contents.find(c => c.key === key)?.content || '';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Foto di Gruppo del Team</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={getContent('team_image')}
                onChange={(e) => saveContent.mutate({ key: 'team_image', content: e.target.value })}
                placeholder="URL immagine team"
              />
              <Button type="button" variant="outline" onClick={() => document.getElementById('team-image-upload').click()}>
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
              <input
                id="team-image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileUpload('team_image', e.target.files?.[0])}
              />
            </div>
            {getContent('team_image') && (
              <img src={getContent('team_image')} alt="Team" className="mt-2 max-h-64 rounded object-cover" />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Video Presentazione</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={getContent('video_url')}
                onChange={(e) => saveContent.mutate({ key: 'video_url', content: e.target.value })}
                placeholder="URL video"
              />
              <Button type="button" variant="outline" onClick={() => document.getElementById('video-upload').click()}>
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
              <input
                id="video-upload"
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => handleFileUpload('video_url', e.target.files?.[0])}
              />
            </div>
            {getContent('video_url') && (
              <video controls className="mt-2 max-h-64 rounded" src={getContent('video_url')}>
                Il tuo browser non supporta il tag video.
              </video>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ServicesManager({ services }) {
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Service.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['services']);
      setEditing(null);
      toast.success('Servizio creato');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Service.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['services']);
      setEditing(null);
      toast.success('Servizio aggiornato');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Service.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['services']);
      toast.success('Servizio eliminato');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editing === 'new') {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate({ id: editing, data: formData });
    }
  };

  if (editing) {
    return (
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Titolo</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Ordine</Label>
              <Input
                type="number"
                value={formData.order || 0}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit">Salva</Button>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Button onClick={() => { setEditing('new'); setFormData({ title: '', description: '', order: 0 }); }} className="mb-4">
        <Plus className="w-4 h-4 mr-2" /> Nuovo Servizio
      </Button>

      <div className="grid gap-4">
        {services.map((service) => (
          <Card key={service.id}>
            <CardContent className="flex items-start justify-between p-4">
              <div>
                <h3 className="font-medium">{service.title}</h3>
                <p className="text-sm text-[#7a7a7a]">{service.description}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => { setEditing(service.id); setFormData(service); }}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="destructive" size="icon" onClick={() => deleteMutation.mutate(service.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function TeamManager({ team }) {
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.TeamMember.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['team']);
      setEditing(null);
      toast.success('Membro aggiunto');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.TeamMember.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['team']);
      setEditing(null);
      toast.success('Membro aggiornato');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.TeamMember.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['team']);
      toast.success('Membro rimosso');
    },
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setFormData(prev => ({ ...prev, image: file_url }));
      toast.success('Immagine caricata');
    } catch (error) {
      toast.error('Errore nel caricamento');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editing === 'new') {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate({ id: editing, data: formData });
    }
  };

  if (editing) {
    return (
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Input
                value={formData.role || ''}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Immagine</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.image || ''}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="URL immagine"
                />
                <Button type="button" variant="outline" onClick={() => document.getElementById('team-upload').click()}>
                  <Upload className="w-4 h-4" />
                </Button>
                <input
                  id="team-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
              {formData.image && (
                <img src={formData.image} alt="" className="mt-2 h-32 w-32 object-cover rounded" />
              )}
            </div>
            <div>
              <Label>Ordine</Label>
              <Input
                type="number"
                value={formData.order || 0}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit">Salva</Button>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Button onClick={() => { setEditing('new'); setFormData({ name: '', role: '', image: '', order: 0 }); }} className="mb-4">
        <Plus className="w-4 h-4 mr-2" /> Nuovo Membro
      </Button>

      <div className="grid gap-4">
        {team.map((member) => (
          <Card key={member.id}>
            <CardContent className="flex items-center gap-4 p-4">
              {member.image && (
                <img src={member.image} alt={member.name} className="w-16 h-16 object-cover rounded-full" />
              )}
              <div className="flex-1">
                <h3 className="font-medium">{member.name}</h3>
                <p className="text-sm text-[#7a7a7a]">{member.role}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => { setEditing(member.id); setFormData(member); }}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="destructive" size="icon" onClick={() => deleteMutation.mutate(member.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}